/**
 * Medical Dictionary API Client
 * CRUD operations for medical dictionary (anatomy, modality, contrast, technique)
 */
import api from './api';

// Types
export interface DictionaryEntry {
  id: string;
  term_ro: string;
  term_canonical: string;
  entity_type: 'anatomy' | 'modality' | 'contrast' | 'technique';
  source: string;
  added_by: string | null;
  added_at: string;
  updated_by: string | null;
  updated_at: string | null;
}

export interface DictionaryListResponse {
  items: DictionaryEntry[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface CreateDictionaryEntry {
  term_ro: string;
  term_canonical: string;
  entity_type: 'anatomy' | 'modality' | 'contrast' | 'technique';
  source?: string;
}

export interface UpdateDictionaryEntry {
  term_canonical: string;
}

export interface DictionaryImportResponse {
  created: number;
  updated: number;
  skipped: number;
  errors: string[];
}

// API Functions
const BASE_URL = '/medical-dictionary';

/**
 * List dictionary entries with pagination and filters
 */
export const listDictionaryEntries = async (params?: {
  entity_type?: string;
  search?: string;
  page?: number;
  limit?: number;
}): Promise<DictionaryListResponse> => {
  const response = await api.get(BASE_URL, { params });
  return response.data;
};

/**
 * Get single dictionary entry by ID
 */
export const getDictionaryEntry = async (id: string): Promise<DictionaryEntry> => {
  const response = await api.get(`${BASE_URL}/${id}`);
  return response.data;
};

/**
 * Create new dictionary entry
 */
export const createDictionaryEntry = async (
  data: CreateDictionaryEntry
): Promise<DictionaryEntry> => {
  const response = await api.post(BASE_URL, data);
  return response.data;
};

/**
 * Update dictionary entry (only term_canonical)
 */
export const updateDictionaryEntry = async (
  id: string,
  data: UpdateDictionaryEntry
): Promise<DictionaryEntry> => {
  const response = await api.put(`${BASE_URL}/${id}`, data);
  return response.data;
};

/**
 * Delete dictionary entry (hard delete with audit)
 */
export const deleteDictionaryEntry = async (
  id: string,
  reason?: string
): Promise<void> => {
  await api.delete(`${BASE_URL}/${id}`, {
    params: reason ? { reason } : undefined,
  });
};

/**
 * Invalidate Redis cache (admin only)
 */
export const invalidateDictionaryCache = async (
  entity_type?: string
): Promise<void> => {
  await api.post(`${BASE_URL}/cache/invalidate`, null, {
    params: entity_type ? { entity_type } : undefined,
  });
};

/**
 * Batch create multiple entries (helper)
 */
export const batchCreateDictionaryEntries = async (
  entries: CreateDictionaryEntry[]
) => {
  const results = {
    created: 0,
    failed: 0,
    errors: [] as string[],
  };

  for (const entry of entries) {
    try {
      await createDictionaryEntry(entry);
      results.created++;
    } catch (error: any) {
      results.failed++;
      results.errors.push(
        `Failed to create "${entry.term_ro}": ${error.message || 'Unknown error'}`
      );
    }
  }

  return results;
};

/**
 * Export dictionary to CSV (Excel-friendly)
 */
export const exportDictionaryCsv = async (entity_type?: string) => {
  const response = await api.get(`${BASE_URL}/export/csv`, {
    params: entity_type ? { entity_type } : undefined,
    responseType: 'blob',
  });
  return response.data;
};

/**
 * Preview CSV file before importing
 */
export const previewCsvImport = async (file: File) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post(`${BASE_URL}/import/csv/preview`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

/**
 * Import dictionary from CSV file
 */
export const importDictionaryCsv = async (
  file: File,
  mode: 'skip' | 'replace' | 'update' = 'skip'
) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('mode', mode);

  const response = await api.post(`${BASE_URL}/import/csv?mode=${mode}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export default {
  listDictionaryEntries,
  getDictionaryEntry,
  createDictionaryEntry,
  updateDictionaryEntry,
  deleteDictionaryEntry,
  invalidateDictionaryCache,
  batchCreateDictionaryEntries,
  exportDictionaryCsv,
  previewCsvImport,
  importDictionaryCsv,
};

